import {
    Component,
    Input,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    signal,
    OnInit,
    ElementRef,
    Output,
    EventEmitter,
    inject,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Invoice, InvoiceUpdateData, lineItemType, Occurrence } from "../../pojo/Pricing";
import { UserprefService } from "../../services/userpref.service";
import { S25PricingUtil } from "./s25.pricing.util";
import { S25Util } from "../../util/s25-util";
import { PricingService } from "../../services/pricing.service";
import { Bind } from "../../decorators/bind.decorator";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { S25PricingStore } from "./s25.pricing.store";

@TypeManagerDecorator("included-occurrences")
@Component({
    selector: "included-occurrences",
    template: `
        <s25-loading-inline></s25-loading-inline>
        @if (init()) {
            <div
                class="included-container"
                [ngClass]="{
                    'invoice-dash-view': isDashView,
                    'date-range': dateRange && !isIncludeAll,
                    'include-all': isIncludeAll,
                }"
            >
                @let invoiceView = isDashView && !showBilledOccs();
                @let invoiceCompleted = !!selectedInvoice?.billDefn.completed;
                @if (dateRange && !isIncludeAll && (invoiceView || !isDashView)) {
                    <div class="date-content" [class.remove-display]="showOccsToBeRemoved()">
                        <div class="dates-container">
                            @if (isDashView && !showBilledOccs() && dateRange) {
                                <h3>Included Occurrences</h3>
                            }
                            <label>
                                Start
                                <s25-ng-editable-date
                                    class="c-margin-left--quarter"
                                    [(val)]="dateRange.start"
                                    [readOnly]="!canEditPricing || invoiceCompleted"
                                    (valChange)="onDateChange()"
                                ></s25-ng-editable-date>
                            </label>
                            <label>
                                End
                                <s25-ng-editable-date
                                    class="c-margin-left--quarter"
                                    [(val)]="dateRange.end"
                                    [readOnly]="!canEditPricing || invoiceCompleted"
                                    (valChange)="onDateChange()"
                                ></s25-ng-editable-date>
                            </label>
                        </div>
                        @if (showOccsToBeRemoved() && occsToBeRemoved.length) {
                            <div class="occ-border"></div>
                            <div class="remove-occs">
                                <div class="header-wrapper">
                                    <h3>Removed Occurrences</h3>
                                    <s25-ng-info-message [isPopover]="true"
                                        >Selecting this date range will result in the removal of the following
                                        occurrences from this invoice and their addition to Non-Invoiced
                                        Billing</s25-ng-info-message
                                    >
                                </div>
                                <ul>
                                    @for (occ of occsToBeRemoved; track occ.rsrvId) {
                                        <li class="c-margin-bottom--quarter">
                                            <s25-ng-icon [type]="'calendarCalendar'"></s25-ng-icon>
                                            {{ occ.occurrence }}
                                        </li>
                                    }
                                </ul>
                            </div>
                        }
                    </div>
                    @if (dateRangeError()) {
                        <p class="error-message">{{ dateRangeError() }}</p>
                    }

                    @if (isDashView && !updateDateDisabled() && canEditPricing && !invoiceCompleted) {
                        <s25-ng-button [type]="'primary'" [onClick]="updateRange">Edit Date Range</s25-ng-button>
                    }
                } @else if (isDashView && showBilledOccs()) {
                    <ng-container [ngTemplateOutlet]="billedOccsList"></ng-container>
                    <button class="aw-button aw-button--primary" (click)="resetDateRange()">Adjust Dates</button>
                } @else if (selectedInvoice) {
                    <h3>Included Occurrences</h3>
                    <ul>
                        @if (listedOccurrences.length) {
                            @for (occ of listedOccurrences; track occ.rsrvId) {
                                <li>
                                    @if (editIncludedOccs()) {
                                        <s25-ng-checkbox
                                            [modelValue]="occ.included"
                                            (modelValueChange)="updateIncluded(occ, $event)"
                                            >{{ occ.occurrence }}</s25-ng-checkbox
                                        >
                                    } @else {
                                        <s25-ng-icon [type]="'calendarCalendar'"></s25-ng-icon>
                                        {{ occ.occurrence }}
                                    }
                                </li>
                            }
                        } @else {
                            No occurrences found
                        }
                    </ul>
                    @if (!isIncludeAll && canEditPricing && !invoiceCompleted) {
                        <div class="button-container">
                            <button
                                class="aw-button"
                                [ngClass]="{
                                    'aw-button--primary': !editIncludedOccs(),
                                    'aw-button--outline': editIncludedOccs(),
                                }"
                                (click)="toggleEditOccs()"
                            >
                                {{ editIncludedOccs() ? "Cancel" : "Edit" }}
                            </button>
                            @if (editIncludedOccs()) {
                                <s25-ng-button
                                    [type]="'primary'"
                                    [onClick]="updateOccurrences"
                                    [disabled]="updateOccsDisabled()"
                                    [disabledReason]="'Please update occurrences to save'"
                                    >Save</s25-ng-button
                                >
                            }
                        </div>
                    }
                }
                @if (!isDashView && showBilledOccs()) {
                    <ng-container [ngTemplateOutlet]="billedOccsList"></ng-container>
                }

                <ng-template #billedOccsList>
                    <div class="cn-alert--danger" [class.cn-alert]="!isDashView">
                        <p class="c-margin-bottom--half">
                            The following occurrences in this date range have been billed to other invoices
                        </p>
                        <ul>
                            @for (occ of billedOccs; track occ.rsrvId) {
                                <li class="c-margin-bottom--quarter">
                                    <s25-ng-icon [type]="'calendarCalendarX'"></s25-ng-icon> {{ occ.occurrence }}
                                    <span
                                        class="c-textButton"
                                        (click)="navToInvoice.emit(occ.invoiceId)"
                                        (keydown.enter)="navToInvoice.emit(occ.invoiceId)"
                                        tabindex="0"
                                    >
                                        [{{ occ.invoiceName }}]</span
                                    >
                                </li>
                            }
                        </ul>
                    </div>
                </ng-template>
            </div>
        }
    `,
    host: {
        "[style.--listHeight]":
            "!listedOccurrences?.length ? 'auto' : listedOccurrences?.length > 15 ? '13em' :  '12em'",
        "[style.--billedHeight]": "billedOccs?.length > 15 ? '13em' : billedOccs?.length > 10 ? '12em' : '10em'",
        "[style.flex]": "flexData",
    },
    styles: `
        .included-container {
            padding: 1rem;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            overflow: hidden;
            min-height: 8em;

            h3 {
                margin: 0;
            }

            ul {
                list-style-type: none;
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                min-height: 0;
                max-height: 100%;
                overflow-y: auto;
                column-gap: 1rem !important;
                padding-left: 1rem;
            }

            li {
                display: flex;
                gap: 5px;
            }

            .error-message {
                color: red;
                font-size: 0.9em;
                flex-basis: 100%;
            }

            .cn-alert.cn-alert--danger {
                display: block;
                width: 100%;
                margin: 0;
                height: var(--billedHeight);

                ul {
                    max-height: calc(var(--billedHeight) - 2em);
                }
            }

            .date-content {
                display: flex;
                gap: 10px;

                .occ-border {
                    border-left: 1px solid #708090;
                }

                .remove-occs {
                    position: relative;

                    ul {
                        top: 28px;
                        bottom: 0;
                        left: 0;
                        padding-left: 10px;
                        margin-bottom: 0;
                    }

                    .header-wrapper {
                        display: flex;
                        gap: 3px;
                    }
                }
            }

            .remove-display {
                height: 10em;

                .dates-container {
                    flex-direction: column !important;
                    flex-basis: 25%;
                }

                .remove-occs {
                    flex-basis: 70%;
                }
            }
        }

        .invoice-dash-view {
            box-shadow:
                rgba(0, 0, 0, 0.05) 0 6px 24px 0,
                rgba(0, 0, 0, 0.08) 0 0 0 1px;
            border-radius: 3px;
            height: 100%;
            position: relative;

            ul {
                position: absolute;
                top: 46px;
                bottom: 46px;
                right: 1rem;
                left: 5px;
                gap: 5px;

                li {
                    max-width: 10em;

                    svg,
                    s25-ng-checkbox {
                        stroke-width: 0;
                        position: relative;
                        bottom: 2px;
                    }

                    span {
                        margin-left: 5px !important;
                    }
                }
            }

            .button-container {
                position: absolute;
                bottom: 22px;
                display: flex;
                width: 92%;
                justify-content: space-evenly;
            }

            .dates-container {
                flex-direction: column;
                gap: 5px;
            }

            .error-message {
                flex-basis: unset;
            }

            .cn-alert--danger {
                max-width: 20em;
                font-size: 0.9rem;
                height: 100%;
                position: relative;

                ul {
                    top: 50px;
                    left: 0;
                    bottom: 0;

                    li {
                        max-width: unset;
                    }
                }
            }

            &.include-all {
                ul {
                    bottom: 0;
                }
            }
        }

        .date-range {
            position: static;
            flex-direction: column;
        }

        .dates-container {
            display: flex;
            gap: 2rem;
            flex-wrap: wrap;
        }

        .dates-container label {
            font-weight: 600;
        }

        .dates-container s25-ng-editable-date {
            font-weight: 400;
        }

        @container main-content (width < 1100px) {
            :host {
                .invoice-dash-view {
                    align-items: flex-start;
                    height: var(--listHeight);

                    .date-content {
                        width: 100%;

                        .dates-container {
                            flex-direction: row;
                            gap: 3%;
                            align-items: center;
                            width: 100%;
                        }
                    }

                    .cn-alert--danger {
                        max-width: unset;
                        width: 100%;
                        height: var(--billedHeight);

                        ul {
                            top: 30px;
                        }
                    }

                    s25-ng-button {
                        margin: 0 auto;
                    }
                }

                .remove-display {
                    .dates-container {
                        flex-direction: column !important;
                        flex-basis: 25%;
                    }

                    .remove-occs {
                        flex-basis: 70%;
                    }
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class IncludedOccurrencesComponent implements OnInit {
    @Input() selectedInvoice?: Invoice;
    @Input() canEditPricing: boolean = true;
    @Input({ required: true }) isDashView: boolean;
    @Input() combineRelatedEvents: boolean;
    @Input() eventId: number;

    @Output() updateCreateNewRange = new EventEmitter<{ start: Date; end: Date }>();
    @Output() navToInvoice = new EventEmitter<number>();
    @Output() refreshPricing = new EventEmitter<void>();

    init = signal(false);
    pricingStore = inject(S25PricingStore);
    allInvoices = this.pricingStore.invoices;
    allOccurrences = this.pricingStore.occurrences;
    dateRange: { start: Date; end: Date };
    dateFormat: string;
    timeFormat: string;
    listedOccurrences: (Partial<Occurrence> & { included: boolean })[];
    flexData: string;
    billedOccs: { occurrence: string; invoiceId: number; invoiceName: string; rsrvId: number }[] = [];
    occsToBeRemoved: { occurrence: string; invoiceId: number; invoiceName: string; rsrvId: number }[] = [];
    showBilledOccs = signal(false);
    showOccsToBeRemoved = signal(false);
    isIncludeAll: boolean;
    dateRangeError = signal("");
    editIncludedOccs = signal(false);
    selectedInvoiceRsrvIds: number[];
    invoicedOccurrences: OccData;
    updateOccsDisabled = signal(true);
    updateDateDisabled = signal(true);

    constructor(private elementRef: ElementRef) {}

    async ngOnInit() {
        S25LoadingApi.init(this.elementRef.nativeElement);
        const [dateFormat, timeFormat] = await Promise.all([
            UserprefService.getS25Dateformat(),
            UserprefService.getS25Timeformat(),
        ]);
        this.dateFormat = dateFormat;
        this.timeFormat = timeFormat;
        if (this.selectedInvoice) {
            const { startDt, endDt } = this.selectedInvoice.billDefn;
            if (startDt && endDt)
                this.dateRange = {
                    start: S25Util.date.parse(startDt),
                    end: S25Util.date.parse(endDt),
                };
        } else {
            this.dateRange = {
                start: new Date(),
                end: new Date(),
            };
        }
        this.isIncludeAll = this.selectedInvoice?.billDefn.startDt?.toString().slice(0, 4) === "1900";
        if ((this.selectedInvoice && !this.dateRange) || this.isIncludeAll) this.getListedOccurrences();
        this.flexData = this.getFlexData();
        S25LoadingApi.destroy(this.elementRef.nativeElement);
        this.init.set(true);
    }

    @Bind
    async updateRange() {
        if (!this.validateDates()) return;
        const { start, end } = this.dateRange;
        const [_, error] = await S25Util.Maybe(
            PricingService.putBillingCustom(
                { startDt: S25Util.date.toStartOfDay(start), endDt: S25Util.date.toEndOfDay(end) },
                this.selectedInvoice.id,
            ),
        );
        if (error) return S25Util.showError(error);
        return this.refreshPricing.emit();
    }

    getInvoicedOccurrences() {
        return this.allInvoices().reduce((map: OccData, invoice) => {
            invoice.billing.lineItems.forEach((item) => {
                if (
                    item.occurrences?.length &&
                    item.itemType !== lineItemType.EVENT &&
                    (item.eventId === this.eventId || this.combineRelatedEvents)
                ) {
                    item.occurrences.forEach((occ) => {
                        if (!map[occ.rsrvId]) {
                            map[occ.rsrvId] = {
                                occurrence: "",
                                rsrvId: occ.rsrvId,
                                eventId: item.eventId,
                                billedTo: invoice.billDefn.billName,
                                invoiceId: invoice.id,
                                included: true,
                            };
                        }
                    });
                }
            });
            return map;
        }, {});
    }

    coalesceOccData(occData: OccData) {
        return this.allOccurrences().reduce((map: { [key: number]: Occurrence }, occ: Occurrence) => {
            const isInvoiced = occData[occ.rsrvId];
            if (isInvoiced) {
                occData[occ.rsrvId].eventId = occ.eventId;
                occData[occ.rsrvId].occurrence = occ.occurrence;
            } else if (!isInvoiced && this.editIncludedOccs()) {
                // if editing, show currently billed occurrences along with unbilled to allow option to add
                occData[occ.rsrvId] = {
                    occurrence: occ.occurrence,
                    rsrvId: occ.rsrvId,
                    eventId: occ.eventId,
                    included: false,
                    billedTo: null,
                    invoiceId: null,
                };
            }
            if (this.dateRange) this.checkBillingDates(occ, occData);
            if (occ.eventId === this.eventId || this.combineRelatedEvents) map[occ.rsrvId] = occ;
            return map;
        }, {});
    }

    checkBillingDates(occ: Occurrence, occData: OccData) {
        const isInBilledRange =
            S25Util.date.parse(occ.rsrvStartDt) < S25Util.date.toEndOfDay(this.dateRange.end) &&
            S25Util.date.parse(occ.rsrvEndDt) > S25Util.date.toStartOfDay(this.dateRange.start);
        const { invoiceId, billedTo } = occData[occ.rsrvId] ?? {};
        if (isInBilledRange && occData[occ.rsrvId]) {
            this.billedOccs.push({
                occurrence: occ.occurrence,
                invoiceId: invoiceId,
                invoiceName: billedTo,
                rsrvId: occ.rsrvId,
            });
        } else if (
            !this.showOccsToBeRemoved() &&
            !isInBilledRange &&
            this.selectedInvoice?.id === occData[occ.rsrvId]?.invoiceId
        ) {
            this.occsToBeRemoved.push({
                occurrence: occ.occurrence,
                invoiceId: invoiceId,
                invoiceName: billedTo,
                rsrvId: occ.rsrvId,
            });
        }
    }

    checkRange() {
        this.billedOccs = [];
        const { occDataMap } = this.getOccMap();
        if (this.selectedInvoice)
            this.billedOccs = this.billedOccs.filter((occ) => occ.invoiceId !== this.selectedInvoice.id);
        if (this.billedOccs.length) {
            S25PricingUtil.formatOccDates(this.billedOccs, occDataMap);
            this.showBilledOccs.set(true);
            this.showOccsToBeRemoved.set(false);
            setTimeout(() => {
                this.elementRef.nativeElement.querySelector(".c-textButton")?.focus();
            });
        }
        return !!this.billedOccs.length;
    }

    getListedOccurrences() {
        const { occDataMap, occs } = this.getOccMap();
        const occurrences = Object.values(occs) ?? [];
        //if not editing occurrences, just display...otherwise show billed and unbilled to allow for editing
        this.listedOccurrences = occurrences
            .filter(
                (occ) =>
                    (occ.eventId === this.eventId || this.combineRelatedEvents) &&
                    (occ.invoiceId === this.selectedInvoice.id || (this.editIncludedOccs() && !occ.invoiceId)),
            )
            .sort((a, b) => +new Date(a.occurrence) - +new Date(b.occurrence));
        S25PricingUtil.formatOccDates(this.listedOccurrences, occDataMap);
    }

    getOccMap() {
        this.invoicedOccurrences = this.getInvoicedOccurrences();
        if (!this.selectedInvoiceRsrvIds && this.selectedInvoice) {
            this.selectedInvoiceRsrvIds = Object.values(this.invoicedOccurrences)
                .filter((occ) => occ.invoiceId === this.selectedInvoice.id)
                .map((occ) => occ.rsrvId);
        }
        return {
            occDataMap: {
                ...this.coalesceOccData(this.invoicedOccurrences),
                dateFormat: this.dateFormat,
                timeFormat: this.timeFormat,
            },
            occs: this.invoicedOccurrences,
        };
    }

    validateDates() {
        let invalidDateRange, billedOccs;
        invalidDateRange = this.dateRangeInvalid();
        if (invalidDateRange) {
            this.dateRangeError.set("Start date must be before end date.");
        } else {
            billedOccs = this.checkRange();
            this.dateRangeError.set("");
        }
        return !invalidDateRange && !billedOccs;
    }

    toggleEditOccs() {
        this.editIncludedOccs.update((isEdit) => !isEdit);
        this.getListedOccurrences();
        this.flexData = this.getFlexData();
    }

    @Bind
    async updateOccurrences() {
        const includedOccs = this.listedOccurrences.filter((occ) => occ.included);
        const rsrvIds = includedOccs.map((occ) => occ.rsrvId);
        const { includeEventType, includeRequirements } = this.selectedInvoice.billDefn;
        const payload: Partial<InvoiceUpdateData> = {
            eventIdList: [...new Set(includedOccs.map((occ) => occ.eventId))],
            emptyRsrvList: !rsrvIds.length,
            rsrvIdList: rsrvIds,
            includeSpaces: !!rsrvIds.length,
            includeResources: !!rsrvIds.length,
            includeEventType: includeEventType,
            includeRequirements: includeRequirements,
        };
        const [_, error] = await S25Util.Maybe(PricingService.putBillingCustom(payload, this.selectedInvoice.id));
        if (error) return S25Util.showError(error);
        else return this.refreshPricing.emit();
    }

    updateIncluded(occ: Partial<Occurrence> & { included: boolean }, isChecked: boolean) {
        occ.included = isChecked;
        const rsrvIds = this.listedOccurrences.filter((occ) => occ.included).map((occ) => occ.rsrvId);
        const noChange = S25Util.array.isEqual(rsrvIds?.sort() ?? [], this.selectedInvoiceRsrvIds?.sort() ?? []);
        this.updateOccsDisabled.set(noChange);
    }

    onDateChange() {
        this.showBilledOccs.set(false);
        !!this.selectedInvoice && this.getOccsToBeRemoved();
        if (this.isDashView) {
            const { startDt: prevStart, endDt: prevEnd } = this.selectedInvoice.billDefn;
            const { start: newStart, end: newEnd } = this.dateRange;
            const noChange = S25Util.date.equalDate(prevStart, newStart) && S25Util.date.equalDate(prevEnd, newEnd);
            this.updateDateDisabled.set(noChange);
        } else {
            this.updateCreateNewRange.emit(this.dateRange);
        }
    }

    resetDateRange() {
        const { startDt, endDt } = this.selectedInvoice.billDefn;
        this.dateRange = { start: startDt, end: endDt };
        this.showBilledOccs.set(false);
        this.updateDateDisabled.set(true);
    }

    dateRangeInvalid() {
        return S25Util.date.parse(this.dateRange.start) > S25Util.date.parse(this.dateRange.end);
    }

    getOccsToBeRemoved() {
        this.showOccsToBeRemoved.set(false);
        if (this.dateRangeInvalid()) return;
        this.occsToBeRemoved = [];
        const { occDataMap } = this.getOccMap();
        S25PricingUtil.formatOccDates(this.occsToBeRemoved, occDataMap);
        this.showOccsToBeRemoved.set(!!this.occsToBeRemoved.length);
    }

    getFlexData() {
        if (this.dateRange) {
            return this.showOccsToBeRemoved() ? "0 0 36%" : "unset";
        }
        return `0 0 ${this.listedOccurrences?.length > 3 ? "30%" : "230px"}`;
    }
}

type OccData = {
    [key: number]: Partial<Occurrence> & {
        billedTo: string;
        invoiceId: number;
        included: boolean;
    };
};
